import { alcoveCornerLineFrame, alcoveWallLineFrame, type LineFrame, roomCornerLineFrame, worldOnLine } from '@kaiizen/kai-floor-plan';
import { EDGE_ITEM_TYPES, LINE_EDGE_ITEM_TYPES } from './editorConstants';
import type { EditorMutableState } from './editorState';
import { edgeWorld } from './geometryLib';
import { modelToScreen } from './viewport';

/** A width and height in screen px. */
export interface ScreenSize {
  width: number;
  height: number;
}

/** How far (px) the ⊕ menu opens from its menu point, and how far it keeps inside the stage. */
const ADD_MENU_OFFSET_PX = 14, ADD_MENU_STAGE_PAD_PX = 8;

/** The line the ⊕ menu adds to for a selection other than a wall: an alcove wall, or a room or alcove corner's diagonal. */
function addMenuLine({ selection, shape }: EditorMutableState): LineFrame | null {
  if (selection.kind === 'alcoveWall') return alcoveWallLineFrame(shape, selection.edge, selection.index, selection.wall);
  if (selection.kind === 'corner') return roomCornerLineFrame(shape, selection.corner);
  if (selection.kind === 'alcoveCorner') return alcoveCornerLineFrame(shape, selection.edge, selection.index, selection.which);
  return null;
}

/** Open the ⊕ add-segment menu at the middle of the selected wall's gap, or of the selected line. */
export function openAddMenu(state: EditorMutableState): void {
  if (state.selection.kind === 'edge' && state.edgeGap) {
    const { edge, gs, ge } = state.edgeGap;
    state.addMenu = { ...modelToScreen(state.viewport, edgeWorld(state.shape, edge, (gs + ge) / 2, 0)), edge };
    return;
  }
  const line = addMenuLine(state);
  if (line) state.addMenu = { ...modelToScreen(state.viewport, worldOnLine(line, line.length / 2, 0)), edge: 'backEdge' };
}

/** The edge-item kinds the ⊕ menu offers: a line edge (an alcove wall, a room or alcove corner's diagonal) can't host an alcove. */
export const addMenuItemTypes = ({ selection }: EditorMutableState): typeof EDGE_ITEM_TYPES =>
  selection.kind === 'alcoveWall' || selection.kind === 'corner' || selection.kind === 'alcoveCorner' ? LINE_EDGE_ITEM_TYPES : EDGE_ITEM_TYPES;

/**
 * Where the open ⊕ menu of `menuSize` sits on the stage: centered across its
 * menu point, opening away from its host edge (up for the front wall, down
 * otherwise), and kept inside the stage. Null while the menu is closed. On a
 * stage smaller than the menu, the menu keeps the pad on its top and left.
 */
export function addMenuLayout({ addMenu }: EditorMutableState, menuSize: ScreenSize, stage: ScreenSize): { left: number; top: number } | null {
  if (!addMenu) return null;
  const top = addMenu.edge === 'frontEdge' ? addMenu.y - menuSize.height - ADD_MENU_OFFSET_PX : addMenu.y + ADD_MENU_OFFSET_PX;
  return {
    left: Math.max(ADD_MENU_STAGE_PAD_PX, Math.min(addMenu.x - menuSize.width / 2, stage.width - menuSize.width - ADD_MENU_STAGE_PAD_PX)),
    top: Math.max(ADD_MENU_STAGE_PAD_PX, Math.min(top, stage.height - menuSize.height - ADD_MENU_STAGE_PAD_PX)),
  };
}
